import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import {
  CATEGORY_COLORS,
  CATEGORY_COLOR_FALLBACK,
  PAIRED_PARENT_TOOL_LABELS,
  TABLE_SCOPE_TOOL_NAMES,
  formatToolNameAsLabel,
} from "./agentConfig/toolCatalog";
import { TOOL_ICON_MAP } from "./agentConfig/toolPresentation";

function getCategoryColor(title) {
  return CATEGORY_COLORS[title] || CATEGORY_COLOR_FALLBACK;
}

function flattenTools(toolCategories) {
  const out = [];
  toolCategories.forEach((category) => {
    category.tools.forEach((tool) => {
      out.push({ ...tool, categoryTitle: category.title });
    });
  });
  return out;
}

const TOOL_TILE_SIZE_PX = 88;

function buildCategoryTypeLabel(tool) {
  const parts = [tool.categoryTitle];
  if (TABLE_SCOPE_TOOL_NAMES.has(tool.name)) parts.push("scoped");
  if (tool.parentToolName) parts.push("linked");
  return parts.join(" · ");
}

export default function AgentToolLoadout({
  toolCategories,
  inventoryCategories,
  enabledTools,
  onToggleTool,
  toolInstructions = {},
  onUpdateToolInstruction,
  toolSearchQuery,
  setToolSearchQuery,
  loadingToolCatalog = false,
  renderToolConfig,
  renderCategoryConfig,
}) {
  const effectiveInventoryCategories = inventoryCategories || toolCategories;
  const allTools = useMemo(
    () => flattenTools(toolCategories),
    [toolCategories]
  );
  const toolByName = useMemo(
    () => new Map(allTools.map((t) => [t.name, t])),
    [allTools]
  );
  const [detailToolName, setDetailToolName] = useState(null);

  const equippedToolObjects = useMemo(
    () => enabledTools.map((name) => toolByName.get(name)).filter(Boolean),
    [enabledTools, toolByName]
  );

  const instructionsEnabledCount = useMemo(
    () =>
      Object.keys(toolInstructions).filter((name) =>
        enabledTools.includes(name)
      ).length,
    [toolInstructions, enabledTools]
  );

  const detailTool = detailToolName ? toolByName.get(detailToolName) : null;

  const openDetail = (toolName) => setDetailToolName(toolName);
  const closeDetail = () => setDetailToolName(null);

  const handleTileToggle = (tool) => {
    if (tool.disableStandaloneToggle) return;
    onToggleTool(tool.name);
  };

  const renderRow = (tool) => {
    const color = getCategoryColor(tool.categoryTitle);
    const Icon = TOOL_ICON_MAP[tool.name] || BuildOutlinedIcon;
    const isEquipped = enabledTools.includes(tool.name);
    const isScoped = TABLE_SCOPE_TOOL_NAMES.has(tool.name);
    const isPaired = Boolean(tool.parentToolName);
    const isActiveDetail = detailToolName === tool.name;
    const hasInstruction = toolInstructions[tool.name] != null;

    return (
      <Box
        key={tool.name}
        role="button"
        tabIndex={0}
        aria-label={`Open details for ${tool.label}`}
        onClick={(e) => {
          if (e.shiftKey) {
            handleTileToggle(tool);
            return;
          }
          openDetail(tool.name);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            if (e.shiftKey) {
              handleTileToggle(tool);
            } else {
              openDetail(tool.name);
            }
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.25,
          py: 1,
          borderRadius: "6px",
          border: "1px solid",
          borderColor: isActiveDetail
            ? color
            : isEquipped
            ? alpha(color, 0.5)
            : "divider",
          backgroundColor: isEquipped ? alpha(color, 0.06) : "transparent",
          boxShadow: isEquipped ? `inset 2px 0 0 ${color}` : "none",
          cursor: "pointer",
          transition:
            "background-color 0.12s, border-color 0.12s, box-shadow 0.12s",
          "&:hover": {
            borderColor: color,
            backgroundColor: isEquipped
              ? alpha(color, 0.1)
              : alpha(color, 0.04),
          },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "6px",
            backgroundColor: alpha(color, 0.12),
            color,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: isEquipped ? 600 : 500,
                color: "text.primary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {tool.label}
            </Typography>
            {isScoped && (
              <Tooltip title="Scoped to specific tables" arrow placement="top">
                <TuneIcon sx={{ fontSize: 13, color: "warning.main" }} />
              </Tooltip>
            )}
            {isPaired && (
              <Tooltip
                title={`Auto-equipped with ${
                  PAIRED_PARENT_TOOL_LABELS[tool.parentToolName] ||
                  formatToolNameAsLabel(tool.parentToolName)
                }`}
                arrow
                placement="top"
              >
                <LinkOutlinedIcon
                  sx={{ fontSize: 13, color: alpha(color, 0.8) }}
                />
              </Tooltip>
            )}
            {hasInstruction && (
              <Tooltip title="Custom instructions set" arrow placement="top">
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "warning.main",
                  }}
                />
              </Tooltip>
            )}
          </Box>
          {tool.desc && (
            <Typography
              sx={{
                fontSize: 11,
                color: "text.secondary",
                mt: 0.25,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {tool.desc}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderTile = (tool) => {
    const color = getCategoryColor(tool.categoryTitle);
    const Icon = TOOL_ICON_MAP[tool.name] || BuildOutlinedIcon;
    const isEquipped = enabledTools.includes(tool.name);
    const isScoped = TABLE_SCOPE_TOOL_NAMES.has(tool.name);
    const isPaired = Boolean(tool.parentToolName);
    const isActiveDetail = detailToolName === tool.name;

    return (
      <Tooltip
        key={tool.name}
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {tool.label}
            </Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.75, mt: 0.25 }}>
              {buildCategoryTypeLabel(tool)}
            </Typography>
            {tool.desc && (
              <Typography sx={{ fontSize: 11, mt: 0.5, lineHeight: 1.4 }}>
                {tool.desc}
              </Typography>
            )}
            {isPaired && (
              <Typography
                sx={{ fontSize: 10, mt: 0.5, color: "warning.light" }}
              >
                Auto-equipped with{" "}
                {PAIRED_PARENT_TOOL_LABELS[tool.parentToolName] ||
                  formatToolNameAsLabel(tool.parentToolName)}
              </Typography>
            )}
          </Box>
        }
        arrow
        placement="top"
        enterDelay={300}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-label={`Open details for ${tool.label}`}
          onClick={(e) => {
            if (e.shiftKey) {
              handleTileToggle(tool);
              return;
            }
            openDetail(tool.name);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
              e.preventDefault();
              if (e.shiftKey) {
                handleTileToggle(tool);
              } else {
                openDetail(tool.name);
              }
            }
          }}
          sx={{
            width: TOOL_TILE_SIZE_PX,
            height: TOOL_TILE_SIZE_PX,
            maxWidth: "100%",
            boxSizing: "border-box",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: isActiveDetail
              ? color
              : isEquipped
              ? color
              : alpha(color, 0.25),
            backgroundColor: isEquipped ? alpha(color, 0.08) : "transparent",
            boxShadow: isEquipped ? `inset 2px 0 0 ${color}` : "none",
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            cursor: "pointer",
            transition:
              "transform 0.12s, box-shadow 0.12s, border-color 0.12s, background-color 0.12s",
            "&:hover": {
              transform: "translateY(-1px)",
              borderColor: color,
              backgroundColor: isEquipped
                ? alpha(color, 0.12)
                : alpha(color, 0.05),
              boxShadow: isEquipped
                ? (theme) =>
                    `inset 2px 0 0 ${color}, 0 2px 8px ${alpha(
                      theme.palette.common.black,
                      0.08
                    )}`
                : (theme) =>
                    `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
              zIndex: 1,
            },
          }}
        >
          <Icon sx={{ fontSize: 26, color }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: isEquipped ? 600 : 500,
              color: "text.primary",
              textAlign: "center",
              mt: 0.5,
              lineHeight: 1.2,
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {tool.label}
          </Typography>
          {isScoped && (
            <TuneIcon
              sx={{
                position: "absolute",
                top: 4,
                right: 5,
                fontSize: 12,
                color: "warning.main",
              }}
            />
          )}
          {isPaired && (
            <LinkOutlinedIcon
              sx={{
                position: "absolute",
                bottom: 4,
                left: 5,
                fontSize: 12,
                color: alpha(color, 0.7),
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  };

  // ─── Detail view (inline, replaces inventory) ───
  const renderDetailView = () => {
    if (!detailTool) return null;
    const color = getCategoryColor(detailTool.categoryTitle);
    const Icon = TOOL_ICON_MAP[detailTool.name] || BuildOutlinedIcon;
    const isEquipped = enabledTools.includes(detailTool.name);
    const hasInstruction = toolInstructions[detailTool.name] != null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Detail header with back button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pb: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={closeDetail}
            sx={{ color: "text.secondary" }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "text.secondary",
              cursor: "pointer",
              "&:hover": { color: "text.primary" },
            }}
            onClick={closeDetail}
          >
            Back to tools
          </Typography>
        </Box>

        {/* Tool header */}
        <Box
          sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "8px",
              backgroundColor: alpha(color, 0.12),
              color,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 15, fontWeight: 600, color: "text.primary" }}
            >
              {detailTool.label}
            </Typography>
            <Box
              sx={{
                fontSize: 11,
                color: "text.disabled",
                mt: 0.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: color,
                }}
              />
              <span>{buildCategoryTypeLabel(detailTool)}</span>
            </Box>
          </Box>
          <Box
            component="button"
            disabled={detailTool.disableStandaloneToggle === true}
            onClick={() => {
              if (detailTool.disableStandaloneToggle === true) return;
              handleTileToggle(detailTool);
            }}
            sx={{
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              padding: "6px 16px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: isEquipped ? alpha(color, 0.4) : color,
              backgroundColor: isEquipped ? "background.paper" : color,
              color: isEquipped ? color : "#fff",
              cursor: detailTool.disableStandaloneToggle
                ? "not-allowed"
                : "pointer",
              opacity: detailTool.disableStandaloneToggle ? 0.55 : 1,
              flexShrink: 0,
              transition: "background-color 0.12s, border-color 0.12s",
              "&:hover": {
                backgroundColor: isEquipped ? alpha(color, 0.08) : color,
                borderColor: color,
                filter: isEquipped ? "none" : "brightness(0.9)",
              },
            }}
          >
            {detailTool.disableStandaloneToggle
              ? isEquipped
                ? "Equipped"
                : "Not equipped"
              : isEquipped
              ? "Unequip"
              : "Equip"}
          </Box>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "text.disabled",
                mb: 0.75,
              }}
            >
              Description
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.5 }}
            >
              {detailTool.desc || "No description provided."}
            </Typography>
          </Box>

          {detailTool.parentToolName && (
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 0.75,
                }}
              >
                Dependency
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  backgroundColor: alpha(color, 0.08),
                  border: 1,
                  borderColor: alpha(color, 0.25),
                  borderRadius: "6px",
                  fontSize: 12,
                  color: "text.secondary",
                }}
              >
                <LinkOutlinedIcon sx={{ fontSize: 16, color }} />
                <span>
                  Automatically equipped with{" "}
                  {PAIRED_PARENT_TOOL_LABELS[detailTool.parentToolName] ||
                    formatToolNameAsLabel(detailTool.parentToolName)}
                  .
                </span>
              </Box>
            </Box>
          )}

          {isEquipped && onUpdateToolInstruction && (
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 0.75,
                }}
              >
                Custom Instructions
              </Typography>
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 0.75,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  size="small"
                  checked={hasInstruction}
                  onChange={(e) => {
                    onUpdateToolInstruction(
                      detailTool.name,
                      e.target.checked ? "" : undefined
                    );
                  }}
                  sx={{
                    p: 0,
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "text.disabled",
                    "&.Mui-checked": { color },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Add custom instructions for this tool
                </Typography>
              </Box>
              {hasInstruction && (
                <TextField
                  className="org-styled-textfield"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  placeholder={`e.g. "Always include a signature" or "Use workflow A for X"`}
                  value={toolInstructions[detailTool.name] || ""}
                  onChange={(e) =>
                    onUpdateToolInstruction(detailTool.name, e.target.value)
                  }
                />
              )}
            </Box>
          )}

          {(() => {
            const configNode = renderToolConfig?.(detailTool);
            if (!configNode) return null;
            return <Box sx={{ mb: 2.5 }}>{configNode}</Box>;
          })()}
        </Box>
      </Box>
    );
  };

  // ─── Inventory view ───
  const renderInventoryView = () => (
    <>
      <Box sx={{ pb: 1.5, borderBottom: 1, borderColor: "divider", mb: 1.5 }}>
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}
        >
          Available Tools
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
          Select tools to equip on this agent
        </Typography>
        <TextField
          className="org-styled-textfield"
          fullWidth
          size="small"
          value={toolSearchQuery}
          onChange={(e) => setToolSearchQuery(e.target.value)}
          placeholder="Search tools..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 1.25 }}
        />
      </Box>

      {loadingToolCatalog ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={20} sx={{ color: "text.secondary" }} />
        </Box>
      ) : effectiveInventoryCategories.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            px: 2,
            py: 1.5,
            backgroundColor: "action.hover",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {toolSearchQuery
              ? `No tools match "${toolSearchQuery.trim()}".`
              : "No tools are available for this agent."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {effectiveInventoryCategories.map((category) => {
            const color = getCategoryColor(category.title);
            const total = category.tools.length;
            const eq = category.tools.filter((t) =>
              enabledTools.includes(t.name)
            ).length;
            const catTools = category.tools.map((t) => ({
              ...t,
              categoryTitle: category.title,
            }));
            const catConfigNode = renderCategoryConfig?.(category);
            return (
              <Box key={category.title}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 1,
                    pb: 0.5,
                    borderBottom: "2px solid",
                    borderColor: alpha(color, 0.4),
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: color,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: "text.primary",
                    }}
                  >
                    {category.title}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 11,
                      color: "text.secondary",
                      backgroundColor: "action.hover",
                      px: 0.75,
                      py: "1px",
                      borderRadius: "10px",
                      ml: 0.25,
                    }}
                  >
                    {eq}/{total}
                  </Box>
                </Box>

                {catConfigNode && (
                  <Box
                    sx={{
                      mb: 1.25,
                      p: 1.5,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: "8px",
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        color: "text.disabled",
                        mb: 1,
                      }}
                    >
                      Shared · Applies to all {category.title} tools
                    </Typography>
                    {catConfigNode}
                  </Box>
                )}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(auto-fill, ${TOOL_TILE_SIZE_PX}px)`,
                    gap: "8px",
                    justifyContent: "flex-start",
                  }}
                >
                  {catTools.map(renderTile)}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "1fr 1fr",
        },
        gap: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "background.paper",
        height: { xs: "auto", md: 560 },
      }}
    >
      {/* Equipped panel (always visible) */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          minWidth: 0,
          borderRight: { xs: 0, md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: "divider",
          backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.4),
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "text.disabled",
            mb: 1.25,
          }}
        >
          Equipped Tools
        </Typography>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {equippedToolObjects.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: "8px",
                px: 2,
                py: 3,
                textAlign: "center",
                fontSize: 12,
                color: "text.disabled",
              }}
            >
              No tools equipped yet. Pick from the right to equip.
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              {equippedToolObjects.map(renderRow)}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 1.5,
            px: 1.25,
            py: 1,
            backgroundColor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: "6px",
            fontSize: 12,
            color: "text.secondary",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {equippedToolObjects.length}
            </Box>{" "}
            equipped
          </span>
          <span>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {instructionsEnabledCount}
            </Box>{" "}
            with custom instructions
          </span>
        </Box>
      </Box>

      {/* Right panel: inventory or detail (inline, no portal) */}
      <Box
        sx={{
          p: 2,
          minWidth: 0,
          minHeight: 420,
          overflowY: "auto",
        }}
      >
        {detailTool ? renderDetailView() : renderInventoryView()}
      </Box>
    </Box>
  );
}
